import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Text, Loader, TextField, Grid, SelectField, Item } from '@pingux/astro';
import ArrowLeftIcon from '@pingux/mdi-react/ArrowLeftIcon';
import { credentialsService } from '@services/credentials';
import {
  Credential,
  CredentialData,
} from '@app-types/tap-partner-vault';
import { useAuthContext } from 'state/context/AuthContext';
import { isGroupOwnersGroup, userIsGroupOwner } from 'utils/auth';

const CredentialEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [credential, setCredential] = useState<Credential | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    state: { user },
  } = useAuthContext();
  const userRole = user?.role ?? 'user';
  const userId = user?.id ?? null;
  const userGroups = user?.groups ?? [];
  const assignableGroups = userGroups.filter((group) => !isGroupOwnersGroup(group));
  const isAdminUser = userRole === 'admin' || userRole === 'superadmin';
  const isGroupOwner = userIsGroupOwner(userGroups);
  const isOwner = Boolean(userId && credential?.owner_id === userId);
  const canEditMetadata = isAdminUser || (isGroupOwner && isOwner);

  const [validationDisabled, setValidationDisabled] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [notes, setNotes] = useState('');
  const [groupTags, setGroupTags] = useState<string[]>([]);
  const [groupTagsInput, setGroupTagsInput] = useState('');
  const [partner, setPartner] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [environment, setEnvironment] = useState<'production' | 'staging' | 'development' | 'sandbox'>('production');

  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [tokenUrl, setTokenUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiKeyUrl, setApiKeyUrl] = useState('');
  const [curlCommand, setCurlCommand] = useState('');
  const [basicUsername, setBasicUsername] = useState('');
  const [basicPassword, setBasicPassword] = useState('');
  const [basicUrl, setBasicUrl] = useState('');
  const [basicCurlCommand, setBasicCurlCommand] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginUrl, setLoginUrl] = useState('');

  const parseGroupTags = (value: string): string[] => (
    Array.from(
      new Set(
        value
          .split(/[\n,]/)
          .map((tag) => tag.trim())
          .filter((tag) => tag.length > 0 && !isGroupOwnersGroup(tag))
      )
    )
  );
  const displayGroupTags = (canEditMetadata ? parseGroupTags(groupTagsInput) : groupTags)
    .filter((tag) => !isGroupOwnersGroup(tag));

  const fetchCredential = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await credentialsService.get(id);
      setCredential(data);
      setPartner(data.partner);
      setTenantId(data.tenant_id);
      setEnvironment(data.environment);
      setValidationDisabled(data.validation_disabled ?? false);
      setIsPrivate(data.is_private ?? false);
      setNotes(data.notes ?? '');
      const sanitizedTags = (data.group_tags ?? []).filter((tag) => !isGroupOwnersGroup(tag));
      setGroupTags(sanitizedTags);
      setGroupTagsInput(sanitizedTags.join('\n'));

      // Pre-populate non-secret identifiers from metadata
      if (data.credential_type === 'oauth_client') {
        setClientId(data.account_name ?? '');
        setTokenUrl(data.url ?? '');
      } else if (data.credential_type === 'admin_account') {
        setUsername(data.account_name ?? '');
        setLoginUrl(data.url ?? '');
      } else if (data.credential_type === 'api_key') {
        setApiKeyUrl(data.url ?? '');
        setCurlCommand(data.curl_command ?? '');
      } else if (data.credential_type === 'basic_auth') {
        setBasicUsername(data.account_name ?? '');
        setBasicUrl(data.url ?? '');
        setBasicCurlCommand(data.curl_command ?? '');
      }

      // Pre-populate the single secret field
      const secretData = await credentialsService.getSecret(id);
      if (data.credential_type === 'oauth_client') {
        setClientSecret(secretData.secret);
      } else if (data.credential_type === 'api_key') {
        setApiKey(secretData.secret);
      } else if (data.credential_type === 'admin_account') {
        setPassword(secretData.secret);
      } else if (data.credential_type === 'basic_auth') {
        setBasicPassword(secretData.secret);
      }
    } catch (err) {
      setError('Failed to load credential for editing.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCredential();
  }, [fetchCredential]);

  const buildUpdatedSecret = (): CredentialData | null => {
    if (!credential) return null;
    if (credential.credential_type === 'oauth_client') {
      if (!clientId.trim() || !clientSecret.trim()) return null;
      return {
        client_id: clientId,
        client_secret: clientSecret,
        token_url: tokenUrl,
      };
    }
    if (credential.credential_type === 'api_key') {
      if (!apiKey.trim()) return null;
      return {
        api_key: apiKey,
        url: apiKeyUrl || undefined,
        curl_command: curlCommand || undefined,
      };
    }
    if (credential.credential_type === 'basic_auth') {
      if (!basicUsername.trim() || !basicPassword.trim()) return null;
      return {
        username: basicUsername,
        password: basicPassword,
        url: basicUrl || undefined,
        curl_command: basicCurlCommand || undefined,
      };
    }
    if (!username.trim() || !password.trim()) return null;
    return {
      username,
      password,
      login_url: loginUrl,
    };
  };

  const handleSubmit = useCallback(async () => {
    if (!id) return;
    const updated = buildUpdatedSecret();
    if (!updated) {
      setError('Please fill in all required fields before saving.');
      return;
    }
    const nextGroupTags = parseGroupTags(groupTagsInput);
    if (canEditMetadata && nextGroupTags.length === 0) {
      setError('At least one group tag is required.');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      await credentialsService.update(id, {
        credentials: updated,
        partner: canEditMetadata ? partner : undefined,
        tenant_id: canEditMetadata ? tenantId : undefined,
        environment: canEditMetadata ? environment : undefined,
        group_tags: canEditMetadata ? nextGroupTags : undefined,
        validation_disabled: validationDisabled,
        notes: notes.trim() || undefined,
        is_private: isPrivate,
      });
      navigate(`/credentials/${id}`);
    } catch (err) {
      setError('Failed to update credential. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [id, buildUpdatedSecret, canEditMetadata, partner, tenantId, environment, groupTagsInput, validationDisabled, notes, isPrivate, navigate]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <Loader />
      </Box>
    );
  }

  if (!credential) {
    return (
      <Box sx={{ padding: '1.5rem 0' }}>
        <Box
          sx={{
            padding: '2rem',
            textAlign: 'center',
            backgroundColor: 'white',
            borderRadius: '0.5rem',
          }}
        >
          <Text sx={{ color: '#6b7280' }}>Credential not found</Text>
          <Button variant="link" onPress={() => navigate('/')} sx={{ marginTop: '1rem' }}>
            Back to Dashboard
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: '1.5rem 0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <Button variant="link" onPress={() => navigate(`/credentials/${credential.id}`)} sx={{ padding: '0.5rem' }}>
          <ArrowLeftIcon size={20} />
        </Button>
        <Box>
          <Text as="h1" sx={{ fontSize: '1.5rem', fontWeight: '600', color: '#111827' }}>
            Edit Credential
          </Text>
          <Text sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Update secret values for this credential
          </Text>
        </Box>
      </Box>

      {error && (
        <Box
          sx={{
            padding: '1rem',
            backgroundColor: '#fee2e2',
            borderRadius: '0.5rem',
            marginBottom: '1.5rem',
            borderLeft: '4px solid #dc2626',
          }}
        >
          <Text sx={{ color: '#991b1b' }}>{error}</Text>
        </Box>
      )}

      <Box
        sx={{
          padding: '1.5rem',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          marginBottom: '1.5rem',
        }}
      >
        {canEditMetadata && (
          <>
            <Text
              sx={{
                fontSize: '1rem',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '1rem',
                paddingBottom: '0.5rem',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              Metadata
            </Text>
            <Grid columns={[1, 1, 2]} gap="lg" style={{ marginBottom: '1.5rem' }}>
              <TextField
                label="Partner"
                value={partner}
                onChange={(e) => setPartner(e.target.value)}
              />
              <TextField
                label="Tenant ID"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
              />
              <SelectField
                label="Environment"
                selectedKey={environment}
                onSelectionChange={(key) =>
                  setEnvironment(key as 'production' | 'staging' | 'development' | 'sandbox')
                }
              >
                {['production', 'staging', 'development', 'sandbox'].map((env) => (
                  <Item key={env}>{env.charAt(0).toUpperCase() + env.slice(1)}</Item>
                ))}
              </SelectField>
            </Grid>
          </>
        )}
        <Text
          sx={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '1rem',
            paddingBottom: '0.5rem',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          {credential.credential_type.replace('_', ' ')} Secrets
        </Text>
        <Grid columns={[1, 1, 2]} gap="lg">
          {credential.credential_type === 'oauth_client' && (
            <>
              <TextField
                label="Client ID"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              />
              <TextField
                label="Client Secret"
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
              />
              <TextField
                label="Token URL"
                value={tokenUrl}
                onChange={(e) => setTokenUrl(e.target.value)}
              />
            </>
          )}
          {credential.credential_type === 'api_key' && (
            <>
              <TextField
                label="API Key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
              <TextField
                label="URL (Optional)"
                value={apiKeyUrl}
                onChange={(e) => setApiKeyUrl(e.target.value)}
                placeholder="https://api.example.com"
              />
              <Box>
                <Text sx={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                  Validation Curl Command (Optional)
                </Text>
                <textarea
                  value={curlCommand}
                  onChange={(e) => setCurlCommand(e.target.value)}
                  placeholder={'curl https://api.example.com/v1/models \\\n  -H "Authorization: Bearer YOUR_API_KEY"'}
                  rows={3}
                  style={{
                    width: '100%',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
                <Text sx={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  Paste the curl command used to call the API, replacing your actual key with YOUR_API_KEY
                </Text>
              </Box>
            </>
          )}
          {credential.credential_type === 'admin_account' && (
            <>
              <TextField
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <TextField
                label="Login URL"
                value={loginUrl}
                onChange={(e) => setLoginUrl(e.target.value)}
              />
            </>
          )}
          {credential.credential_type === 'basic_auth' && (
            <>
              <TextField
                label="Username"
                value={basicUsername}
                onChange={(e) => setBasicUsername(e.target.value)}
              />
              <TextField
                label="Password"
                type="password"
                value={basicPassword}
                onChange={(e) => setBasicPassword(e.target.value)}
              />
              <TextField
                label="URL (Optional)"
                value={basicUrl}
                onChange={(e) => setBasicUrl(e.target.value)}
                placeholder="https://example.com"
              />
              <Box>
                <Text sx={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                  Validation Curl Command (Optional)
                </Text>
                <textarea
                  value={basicCurlCommand}
                  onChange={(e) => setBasicCurlCommand(e.target.value)}
                  placeholder={`curl -H "Authorization: Basic $(echo -n '<USERNAME>:<PASSWORD>' | base64)" https://example.com`}
                  rows={3}
                  style={{
                    width: '100%',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
                <Text sx={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  Paste the curl command used to validate credentials. The server will substitute the actual base64-encoded credentials automatically.
                </Text>
              </Box>
            </>
          )}
        </Grid>
      </Box>

      <Box
        sx={{
          padding: '1.5rem',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          marginBottom: '1.5rem',
        }}
      >
        <Text
          sx={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '1rem',
            paddingBottom: '0.5rem',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          Group Tags
        </Text>
        {canEditMetadata && isAdminUser ? (
          <>
            <textarea
              value={groupTagsInput}
              onChange={(e) => setGroupTagsInput(e.target.value)}
              rows={4}
              placeholder={'Admins\nPartner Engineering'}
              style={{
                width: '100%',
                fontFamily: 'inherit',
                fontSize: '0.875rem',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
            <Text sx={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
              Enter one exact PingOne group name per line, or separate values with commas.
            </Text>
          </>
        ) : canEditMetadata ? (
          <>
            <select
              multiple
              value={groupTags.filter((tag) => !isGroupOwnersGroup(tag))}
              onChange={(e) => {
                const next = Array.from(e.target.selectedOptions, (option) => option.value);
                setGroupTags(next);
                setGroupTagsInput(next.join('\n'));
              }}
              disabled={assignableGroups.length === 0}
              size={Math.min(Math.max(assignableGroups.length, 3), 8)}
              style={{
                width: '100%',
                fontSize: '0.875rem',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                backgroundColor: assignableGroups.length === 0 ? '#f9fafb' : 'white',
                color: '#111827',
              }}
            >
              {assignableGroups.map((group) => (
                <option key={group} value={group}>
                  {group}
                </option>
              ))}
            </select>
            <Text sx={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
              {assignableGroups.length === 0
                ? 'Your access token does not include any assignable groups.'
                : 'Hold Cmd/Ctrl to select multiple groups.'}
            </Text>
          </>
        ) : (
          <Text sx={{ fontSize: '0.875rem', color: '#374151' }}>
            {displayGroupTags.length > 0 ? displayGroupTags.join(', ') : 'No group tags assigned'}
          </Text>
        )}
        {displayGroupTags.length > 0 && (
          <Box sx={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
            {displayGroupTags.map((tag) => (
              <span
                key={tag}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0.2rem 0.55rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  backgroundColor: '#dbeafe',
                  color: '#1d4ed8',
                }}
              >
                {tag}
              </span>
            ))}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          padding: '1.5rem',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          marginBottom: '1.5rem',
        }}
      >
        <Text
          sx={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '1rem',
            paddingBottom: '0.5rem',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          Notes
        </Text>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any relevant notes about this credential..."
          rows={4}
          style={{
            width: '100%',
            fontFamily: 'inherit',
            fontSize: '0.875rem',
            padding: '0.5rem 0.75rem',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            resize: 'vertical',
            boxSizing: 'border-box',
            color: '#111827',
          }}
        />
      </Box>

      <Box
        sx={{
          padding: '1rem 1.5rem',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
        }}
      >
        <input
          id="is-private"
          type="checkbox"
          checked={isPrivate}
          onChange={(e) => setIsPrivate(e.target.checked)}
          style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
        />
        <label htmlFor="is-private" style={{ fontSize: '0.875rem', color: '#374151', cursor: 'pointer', userSelect: 'none' }}>
          Private — only visible to me and SuperAdmins
        </label>
      </Box>

      <Box
        sx={{
          padding: '1rem 1.5rem',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
        }}
      >
        <input
          id="validation-disabled"
          type="checkbox"
          checked={validationDisabled}
          onChange={(e) => setValidationDisabled(e.target.checked)}
          style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
        />
        <label htmlFor="validation-disabled" style={{ fontSize: '0.875rem', color: '#374151', cursor: 'pointer', userSelect: 'none' }}>
          Cannot be validated — skip validation for this credential
        </label>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
        <Button variant="default" onPress={() => navigate(`/credentials/${credential.id}`)}>
          Cancel
        </Button>
        <Button variant="primary" onPress={handleSubmit} isLoading={submitting}>
          Save Changes
        </Button>
      </Box>
    </Box>
  );
};

export default CredentialEdit;
